// =============================================================
// docViewer.js — shared document open/view utility
//
// openDocument(doc, api, setViewerState)
//   PDF    → fetches SAS URL, sets viewerState { doc, sasUrl }
//   Office → opens SharePoint edit URL in new tab; if not synced to
//            SharePoint, opens read-only in Office Online via the public
//            viewer (never a silent browser download)
//   Other  → triggers browser download
// =============================================================

const PDF_EXT   = ['pdf'];
const OFFICE_EXT = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];

function getExt(fileName) {
  return (fileName || '').split('.').pop().toLowerCase();
}

export function isPdf(fileName)    { return PDF_EXT.includes(getExt(fileName)); }
export function isOffice(fileName) { return OFFICE_EXT.includes(getExt(fileName)); }

/**
 * Microsoft's public Office Online viewer. Given a publicly-reachable source
 * URL (our blob SAS read URLs qualify), it renders the file in Word / Excel /
 * PowerPoint Online in the browser instead of downloading it. Read-only —
 * editing still requires the SharePoint web/edit link.
 */
export function officeOnlineViewerUrl(sasUrl) {
  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(sasUrl)}`;
}

/**
 * Rewrite a SharePoint Doc.aspx URL so it opens in Office for the web.
 * The stored webUrl carries `action=default`, which lets SharePoint hand off
 * to the tenant/user default app (typically the desktop client, i.e. a
 * download). `?web=1` does not override that — only `action=edit` does.
 */
export function forceOfficeWebEdit(webUrl) {
  if (/[?&]action=/.test(webUrl)) {
    return webUrl.replace(/([?&]action=)[^&]*/, '$1edit');
  }
  const sep = webUrl.includes('?') ? '&' : '?';
  return `${webUrl}${sep}action=edit`;
}

/** Why a document has no SharePoint copy to edit, in words. */
export function notInSharePointReason(status) {
  if (status === 'pending') return "it's still being copied to SharePoint. Try again in a few minutes and it will open for editing.";
  if (status === 'error') return 'copying it to SharePoint failed. An admin can retry it under Settings → Documents → Files not in SharePoint.';
  return 'it has not been copied to SharePoint yet. That now happens automatically within a few minutes; if it persists, an admin can use Retry sync on the document.';
}

/**
 * Opens a document inline (PDF) or in SharePoint (Office), falling back to download.
 *
 * @param {object}   doc           - document record with { documentID, fileName, sharePointItemID }
 * @param {object}   api           - api client with get()
 * @param {function} setViewerState - state setter; called with { doc, sasUrl } for PDFs, null to close
 * @param {function} [onError]     - optional error callback(message); defaults to console.error
 */
export async function openDocument(doc, api, setViewerState, onError) {
  const showError = onError || ((msg) => console.error(msg));
  const ext = getExt(doc.fileName);

  if (PDF_EXT.includes(ext)) {
    try {
      const { sasUrl } = await api.get(`documents/${doc.documentID}/download-url`);
      setViewerState({ doc, sasUrl });
    } catch (e) {
      showError('Could not open document: ' + e.message);
    }
    return;
  }

  if (OFFICE_EXT.includes(ext)) {
    // THE THREE BRANCHES BELOW DEGRADE, AND EVERY STEP DOWN MUST BE ANNOUNCED.
    //
    // Only the first two produce an EDITABLE file; the third is Microsoft's
    // public viewer, which is read-only by construction (it renders a static
    // URL — there is no identity, no lock and no save-back channel, which is
    // why the only thing it can offer is "Edit a copy" into the viewer's own
    // OneDrive). Falling to it silently is indistinguishable, from the user's
    // side, from the file simply not being editable — which is exactly how a
    // failing edit link went unnoticed. So `whyReadOnly` accumulates the reason
    // and the caller is told.
    let whyReadOnly = null;

    // Prefer Graph's createLink sharing URL (the `/:w:/`, `/:x:/`, `/:p:/` form)
    // — that reliably opens the file in Office for the web. The stored raw file
    // webUrl (`.../file.docx`) is NOT the browser-viewer form: SharePoint serves
    // it as a download even with `?web=1` appended, so it's only a last resort.
    if (doc.sharePointItemID) {
      // Open the tab synchronously (inside the click gesture) so the popup
      // blocker doesn't kill it during the createLink round-trip, then point
      // it at the resolved edit URL.
      const win = window.open('', '_blank');
      try {
        const res = await api.get(`documents/${doc.documentID}/edit-url`);
        if (res?.editUrl) {
          if (win) { win.location.href = res.editUrl; } else { window.open(res.editUrl, '_blank'); }
          return;
        }
        // A 200 carrying no editUrl. Graph answered, but with nothing usable.
        whyReadOnly = 'SharePoint returned no edit link for this file';
      } catch (e) {
        // This catch used to be empty. `createLink` is a DIFFERENT Graph
        // operation from the upload that synced the file, with its own
        // permission surface and its own tenant sharing policy, so it can fail
        // on a document that is demonstrably in SharePoint — and swallowing it
        // turned an editable file into a read-only one with nothing said.
        whyReadOnly = `Couldn't get an edit link (${e?.message || e})`;
      }
      if (win) win.close();
    }

    // Fall back to the stored web URL. The persisted SharePoint webUrl is a
    // Doc.aspx link with `action=default`, which defers to the tenant/user's
    // default app (usually the desktop client → a download prompt). Appending
    // `?web=1` does NOT override `action=default`, so force `action=edit` to
    // open the file in Office for the web instead.
    //
    // This still opens an EDITABLE file, so a failure above is not worth a
    // toast — but it is worth a console line, because it is the difference
    // between the preferred path and the fallback and nothing else records it.
    if (doc.sharePointWebUrl) {
      if (whyReadOnly) console.warn(`[docViewer] ${doc.fileName}: ${whyReadOnly}; using the stored SharePoint URL instead.`);
      window.open(forceOfficeWebEdit(doc.sharePointWebUrl), '_blank');
      return;
    }

    // Last resort: the public Office viewer over the blob SAS. READ-ONLY.
    try {
      const { sasUrl } = await api.get(`documents/${doc.documentID}/download-url`);
      window.open(officeOnlineViewerUrl(sasUrl), '_blank');
      if (whyReadOnly) {
        // We had a SharePoint item and still could not get an edit link: this
        // is a fault, not a property of the document, so it is surfaced.
        showError(`${doc.fileName} opened read-only. ${whyReadOnly}.`);
      } else {
        // No SharePoint copy yet. This used to be a console line only, and the
        // result — Microsoft's "Read-only … Edit a copy" banner over a file
        // named by its storage path — read as the app refusing to edit Office
        // files at all. Say why, and what makes it editable.
        showError(`${doc.fileName} opened read-only: ${notInSharePointReason(doc.sharePointSyncStatus)}`);
      }
    } catch (e) {
      showError('Could not open document: ' + e.message);
    }
    return;
  }

  // Default: download
  try {
    const { sasUrl, fileName } = await api.get(`documents/${doc.documentID}/download-url`);
    const a = document.createElement('a');
    a.href = sasUrl; a.download = fileName; a.click();
  } catch (e) {
    showError('Could not download document: ' + e.message);
  }
}
